import * as React from 'react';
import Step, { PrivateStepState, SharedStepState, StepProperties } from '../Step';
import { RightArrowIcon } from '../../common/Icons';
import { Message } from '../../common/Message';
import { filespacePlanTypeMap, ProviderPlanType } from './PlanStep';
import { capitalizeFirstLetter } from '../../../utils/utilFunctions';
import { Nullable } from '../../../utils/types';
import { TransitionType } from '../../../utils/types';

interface EditData {
    currentValue: string | ProviderPlanType | number | null;
    onEdit: () => void;
}

export interface ReviewStepProperties extends StepProperties<ReviewSharedStepState> {
    storageContainerName: string;
    reviewData: {
        plan: EditData,
        name: EditData,
        provider: EditData,
        providerHasRegions: boolean
        region: EditData,
        endpointUrl: EditData,
        endpointRegion: EditData,
        bucketName: EditData | null,
        blockSizeText: EditData | null
        // isV4Selected: boolean
    };
    regionRequired: boolean;
    errorMessage?: string;
    buttonIsLoading: boolean;
    isPlanEditEnabled: boolean;
    domainName: string;
    transitionType: TransitionType;
}

export interface ReviewSharedStepState extends SharedStepState {}
interface ReviewPrivateStepState extends PrivateStepState {}
interface ReviewStepState extends ReviewPrivateStepState, ReviewSharedStepState {}

class ReviewStepComponent extends Step<ReviewStepProperties, ReviewStepState, ReviewSharedStepState> {
    protected getSharedStepState(): ReviewSharedStepState {
        return {};
    }
    constructor(props: ReviewStepProperties) {
        super(props);
        this.determineBetaText = this.determineBetaText.bind(this);
    }

    render() {
        let { plan, name, provider, region, endpointUrl, endpointRegion, bucketName, providerHasRegions, blockSizeText } = this.props.reviewData;
        let errorSection: JSX.Element | null = this.props.errorMessage && this.props.errorMessage.trim() !== '' ?
            (
                <Message message={this.props.errorMessage} />

            ) :
            null;
        const callback = providerHasRegions ? region.onEdit : endpointRegion.onEdit;
        const createButtonIsDisabled =
            (this.props.regionRequired && !this.regionProvided()) ||
            this.props.buttonIsLoading;
        const betaText = this.determineBetaText();
        const planEdit = this.props.isPlanEditEnabled ? (
            <a onClick={plan.onEdit} className="table-row">
                <div>
                    <span>Filespace type</span>
                    <span>{filespacePlanTypeMap[plan.currentValue!]} {betaText}</span>
                </div>
                <span>edit</span>
            </a>
        ) : null;

        return (
            <div className="col-12 col-lg-6 col-xl-6 offset-lg-1 d-flex align-items-center review-step">
                <div className="row review-table">
                    {errorSection}
                    {planEdit}
                    <a onClick={name.onEdit} className="table-row">
                        <div>
                            <span>Filespace name</span>
                            <span>{name.currentValue}<em>{'.' + this.props.domainName}</em></span>
                        </div>
                        <span>edit</span>
                    </a>
                    {
                        plan.currentValue !== ProviderPlanType.LucidLinkCustom ?
                            (
                                <div className="table-row disable">
                                    <div>
                                        <span>Storage provider</span>
                                        <span>{provider.currentValue}</span>
                                    </div>
                                    <span>uneditable</span>
                                </div>
                            ) :
                            null
                    }
                    {
                        plan.currentValue === ProviderPlanType.LucidLinkCustom ?
                            (
                                <a onClick={(provider as EditData).onEdit} className="table-row">
                                    <div>
                                        <span>Storage provider</span>
                                        <span>{provider.currentValue}</span>
                                    </div>
                                    <span>edit</span>
                                </a>
                            ) :
                            null
                    }

                    {
                        region.currentValue && providerHasRegions ?
                            (
                                <a onClick={region.onEdit} className="table-row">
                                    <div>
                                        <span>Storage region</span>
                                        <span>{region.currentValue}</span>
                                    </div>
                                    <span>edit</span>
                                </a>
                            ) :
                            null
                    }
                    {
                        endpointUrl.currentValue && !providerHasRegions ?
                            (
                                <React.Fragment>
                                    <a onClick={endpointUrl.onEdit} className="table-row">
                                        <div>
                                            <span>Endpoint url</span>
                                            <span>{endpointUrl.currentValue}</span>
                                        </div>
                                        <span>edit</span>
                                    </a>
                                    {
                                        endpointRegion.currentValue ?
                                        (
                                            <a onClick={endpointRegion.onEdit} className="table-row">
                                                <div>
                                                    <span>Storage region</span>
                                                    <span>{endpointRegion.currentValue}</span>
                                                </div>
                                                <span>edit</span>
                                            </a>
                                        ) :
                                        null
                                    }
                                </React.Fragment>
                            ) :
                            null
                    }
                    {
                        (
                            (this.props.reviewData.providerHasRegions && !this.regionProvided()) ||
                            (!this.props.reviewData.providerHasRegions && !this.props.reviewData.endpointUrl.currentValue)
                        ) ? (
                                <a onClick={callback} className="table-row">
                                    <span>{this.props.reviewData.providerHasRegions ? 'region' : 'endpoint url'}</span>
                                    <span />
                                    <span>Choose {this.props.reviewData.providerHasRegions ? 'a region' : 'an endpoint'}</span>
                                </a>
                            ) :
                            null
                    }
                    {
                        bucketName ?
                            (
                                <a onClick={bucketName.onEdit} className="table-row">
                                    <div>
                                        <span>{capitalizeFirstLetter(this.props.storageContainerName)}</span>
                                        <span>{bucketName.currentValue}</span>
                                    </div>
                                    <span>edit</span>
                                </a>
                            ) :
                            null
                    }
                    {
                        blockSizeText ?
                            (
                                <a onClick={blockSizeText.onEdit} className="table-row">
                                    <div>
                                        <span>Block size</span>
                                        <span>{blockSizeText.currentValue}</span>
                                    </div>
                                    <span>edit</span>
                                </a>
                            ) :
                            null
                    }

                    <button disabled={createButtonIsDisabled} type="button" className="btn step-btn float-right" onClick={this.completeStep}>
                        <span>{this.props.buttonIsLoading ? 'Creating' : 'Create'}</span>
                        <RightArrowIcon width="13px" height="13px" />
                    </button>
                </div>
            </div>
        );
    }

    private determineBetaText(): Nullable<JSX.Element> {
        // if (this.props.reviewData.isV4Selected && this.props.transitionType === TransitionType.V3ToV4) {
        //     return <em>(beta)</em>;
        // }
        // if (!this.props.reviewData.isV4Selected && this.props.transitionType === TransitionType.None) {
        //     return <em>(deprecated)</em>;
        // }
        return null;
    }

    private regionProvided(): boolean {
        return !!this.props.reviewData.endpointRegion.currentValue || !!this.props.reviewData.region.currentValue;
    }
}

export default ReviewStepComponent;



// WEBPACK FOOTER //
// ./src/webportal/components/wizard/components/ReviewStep.tsx